import { PortForwardingConfig } from '../../Services/serviceURL';

const MS_THRESHOLD_YEAR_2000 = 946684800000;

export type PortFormData = {
  serviceName: string;
  inputPort: string;
  targetPort: string;
  targetIpAddress: string;
  protocol: string;
  accessLimit: string;
  tls: boolean;
};

export type PortFieldErrors = {
  serviceName: string;
  inputPort: string;
  targetPort: string;
  targetIpAddress: string;
};

export const emptyPortFieldErrors = (): PortFieldErrors => ({
  serviceName: '',
  inputPort: '',
  targetPort: '',
  targetIpAddress: '',
});

const RESERVED_INPUT_PORTS = new Set([
  1000, 1001, 1002, 1003, 1004, 1005, 1006, 1007, 1008, 1009, 1010,
  1011, 1012, 1013, 1014, 1015, 1016, 1017, 1018, 1019, 1020, 1021, 1022, 1023, 1024,
  3306, 5432, 1433, 27017, 6379, 5984,
  3389, 5900, 5901, 5902,
  8080, 8443, 8000, 8008, 8888,
  3000, 4000, 5000, 8001, 8002, 8003, 8004, 8005, 8006, 8007, 8009, 8010,
  2049, 2082, 2083, 2086, 2087, 2095, 2096,
]);

const FORBIDDEN_TARGET_IPS = new Set(['127.0.0.1', '0.0.0.0', '255.255.255.255']);

function pluralUnit(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

export function formatAccessLimit(accessLimit?: number): string {
  if (!accessLimit || accessLimit === 0) return 'Unlimited';

  const minutes = Math.floor(accessLimit / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    if (remainingHours > 0) {
      return `${pluralUnit(days, 'day')} ${pluralUnit(remainingHours, 'hour')}`;
    }
    return pluralUnit(days, 'day');
  }

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    if (remainingMinutes > 0) {
      return `${pluralUnit(hours, 'hour')} ${pluralUnit(remainingMinutes, 'minute')}`;
    }
    return pluralUnit(hours, 'hour');
  }

  return pluralUnit(minutes, 'minute');
}

export function formatTLS(tls?: boolean): string {
  return tls ? 'Yes' : '';
}

export function formatRemoteAccessDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateString || '-';
  }
}

export function timestampToMilliseconds(value: number | string): number | null {
  if (typeof value === 'number') {
    const ms = value < MS_THRESHOLD_YEAR_2000 ? value * 1000 : value;
    return isNaN(ms) ? null : ms;
  }
  const parsed = new Date(value).getTime();
  return isNaN(parsed) ? null : parsed;
}

function isPastTimestamp(timestampMs: number, now = Date.now()): boolean {
  return now >= timestampMs;
}

function isExpiredFromStartAndLimit(
  start: number | string | null | undefined,
  accessLimitSeconds: number | undefined,
  logLabel: string,
  config: PortForwardingConfig
): boolean | null {
  if (start == null || !accessLimitSeconds || accessLimitSeconds <= 0) return null;

  try {
    const startMs = timestampToMilliseconds(start);
    if (startMs == null) return null;
    const expirationTime = startMs + accessLimitSeconds * 1000;
    return isPastTimestamp(expirationTime);
  } catch (e) {
    console.error(`Error calculating from ${logLabel}:`, e, config);
    return null;
  }
}

function isExpiredByExpiredTime(config: PortForwardingConfig): boolean | null {
  if (config.expiredTime == null) return null;

  try {
    const expiredTimeMs = timestampToMilliseconds(config.expiredTime as number | string);
    if (expiredTimeMs == null) return null;
    return isPastTimestamp(expiredTimeMs);
  } catch (e) {
    console.error('Error parsing expiredTime:', e);
    return null;
  }
}

function configHasTimestamps(config: PortForwardingConfig): boolean {
  return Boolean(config.createdTime || config.createdOn || config.requestedTime || config.expiredTime);
}

export function isConfigExpired(config: PortForwardingConfig): boolean {
  const fromExpiredTime = isExpiredByExpiredTime(config);
  if (fromExpiredTime !== null) return fromExpiredTime;

  const fromCreated = isExpiredFromStartAndLimit(config.createdTime, config.accessLimit, 'createdTime', config);
  if (fromCreated !== null) return fromCreated;

  const fromCreatedOn = isExpiredFromStartAndLimit(config.createdOn, config.accessLimit, 'createdOn', config);
  if (fromCreatedOn !== null) return fromCreatedOn;

  const fromRequested = isExpiredFromStartAndLimit(config.requestedTime, config.accessLimit, 'requestedTime', config);
  if (fromRequested !== null) return fromRequested;

  if (config.expired === true) return true;

  if (config.expired === false && !configHasTimestamps(config)) return false;

  if (configHasTimestamps(config)) {
    console.warn('Timestamps exist but calculation failed - defaulting to expired for safety', config);
    return true;
  }

  if (config.serviceName || config.inputPort || config.id) {
    console.warn('Config has no expiration info - defaulting to expired for safety');
    return true;
  }

  return false;
}

export function clonePortForwardingConfigs(configs: PortForwardingConfig[]): PortForwardingConfig[] {
  return configs.map((config) => ({ ...config }));
}

export type PortFormValidationResult =
  | { ok: true; inputPort: number; targetPort: number }
  | { ok: false; fieldErrors?: PortFieldErrors; toastMessage: string };

function validateIpAddress(ip: string): string | null {
  const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (!ipRegex.test(ip)) {
    return 'Please enter a valid IP address (e.g., 192.168.1.100)';
  }

  const octets = ip.split('.');
  for (const part of octets) {
    const octet = parseInt(part, 10);
    if (octet < 0 || octet > 255) {
      return 'IP address octets must be between 0 and 255';
    }
  }

  if (FORBIDDEN_TARGET_IPS.has(ip)) {
    return 'Target IP address cannot be localhost, broadcast, or null address';
  }

  return null;
}

export function validatePortForwardingForm(form: PortFormData): PortFormValidationResult {
  const errors = emptyPortFieldErrors();

  if (!form.inputPort) errors.inputPort = 'Please fill required field';
  if (!form.targetPort) errors.targetPort = 'Please fill required field';
  if (!form.targetIpAddress) errors.targetIpAddress = 'Please fill required field';

  if (errors.inputPort || errors.targetPort || errors.targetIpAddress) {
    return { ok: false, fieldErrors: errors, toastMessage: '' };
  }

  const inputPort = parseInt(form.inputPort, 10);
  const targetPort = parseInt(form.targetPort, 10);

  if (isNaN(inputPort) || inputPort < 1000 || inputPort > 9999) {
    return { ok: false, toastMessage: 'Input port must be a 4-digit number (1000-9999)' };
  }

  if (RESERVED_INPUT_PORTS.has(inputPort)) {
    return {
      ok: false,
      toastMessage: `Input port ${inputPort} is reserved by the system. Please try ports in 6000-6999 or 7000-7999 range.`,
    };
  }

  if (isNaN(targetPort) || targetPort < 1 || targetPort > 65535) {
    return { ok: false, toastMessage: 'Target port must be a valid port number (1-65535)' };
  }

  const ipError = validateIpAddress(form.targetIpAddress);
  if (ipError) {
    return { ok: false, toastMessage: ipError };
  }

  return { ok: true, inputPort, targetPort };
}
